/**
 * Location step — ONE screen, three variants by the service descriptor's
 * pricingMode (registry-driven; manual services never come here):
 *  - grid/airport: gazetteer search (placeholder kept VERBATIM — a shared
 *    e2e fixture types into it), tap result → passengers.
 *  - grid/golf: a PLACE at each end over one shared list (T906 S1) → passengers.
 *    It used to be hub buttons → routes fetched from /api/golf-routes, both
 *    derived from the rates table, so an unpriced pair had no tile and could not
 *    be quoted at all. See GolfVariant.
 *  - p2p (local-other): pickup + REQUIRED drop-off, Continue gated on BOTH —
 *    a blank drop-off cannot leave this screen (the bug-c client half,
 *    fix-by-construction; N2 pins it).
 */
import { useState } from 'preact/hooks';
import { Chip, EmptyState, FormField } from '@ecmb/ui';
import { LocationSearch } from '../components/LocationSearch.js';
import { TotalBar } from '../components/TotalBar.js';
import { closureNoteFor } from '../../shared/course-closures.js';
// T2090 - the airport direction and terminal vocabulary, from the registry's
// own enums (never a local list), and the pressed-state shape `airportEndOf`
// derives from the job.
import { AIRPORT_TERMINALS, terminalDesignation, type AirportDirection, type AirportTerminal } from '../../shared/service-schema.js';
import type { AirportEnd } from '../lib/actions.js';
import type { Registry } from '../store/registry.js';

/** Kept verbatim: fixtures/journey.ts:58 types into this placeholder. */
export const AIRPORT_SEARCH_PLACEHOLDER = 'Type to filter locations...';

export type LocationScreenProps = {
  registry: Registry;
  service: string;
  pricingMode: 'grid' | 'p2p';
  pickupLabel: string | null;
  dropoffLabel: string | null;
  pickupText?: string | null;
  dropoffText?: string | null;
  /** T385 item 5 — routing key from the inquiry's address-end Eircode.
   *  Suggestion-grade AREA hint on the pickup search only: display line +
   *  ordering, never a filter, never an auto-pick. */
  routingKey?: string | null;
  onPickLocation: (id: string, label: string) => void;
  onClearLocation: () => void;
  onPickDropoff: (id: string, label: string) => void;
  onClearDropoff: () => void;
  /** T373 M4 — hand-quote an off-gazetteer pickup / drop-off with its true name. */
  onEscapePickup: (place: string) => void;
  onEscapeDropoff: (place: string) => void;
  /** T385 item 6 — a gazetteer miss reached a terminal moment (escape taken /
   *  search abandoned); the app logs it as unresolved demand evidence. */
  onMiss?: (typedText: string) => void;
  /** T906 S1 — the two stated golf ends, and the one writer for them. `null`
   *  clears an end. The writer re-resolves the PAIR, so it owns the price. */
  golfPickupPlace?: string | null;
  golfDestinationPlace?: string | null;
  onSetGolfEnd: (end: 'pickup' | 'destination', place: string | null) => void;
  /** T2090 - the airport journey's direction and terminal as the job carries
   *  them (`airportEndOf`), and the one writer (`setAirportEnd`). Optional so
   *  every existing mount compiles; with neither, nothing is pressed. */
  airportEnd?: AirportEnd;
  onSetAirportEnd?: (direction: AirportDirection, terminal: AirportTerminal | null) => void;
  onContinue: () => void;
};

/** The direction chips, in journey order. The words are the site form's own
 *  question restated for Helen, who is on the phone with the customer. */
const AIRPORT_DIRECTION_WORD: Record<AirportDirection, string> = {
  'to-airport': 'Going to the airport',
  'from-airport': 'Coming from the airport',
};

/** One golf place as the registry publishes it — identity is the NAME. */
type GolfPlace = { name: string; kind: 'hub' | 'course' | 'airport' };

export function LocationScreen(props: LocationScreenProps) {
  const golf = props.service === 'golf';
  const p2p = props.pricingMode === 'p2p';

  if (golf) return <GolfVariant {...props} />;

  // P0-27 — the priced towns, browsable without typing. Already loaded at login
  // (no new endpoint, no new fetch); M9 widened the row with the area to group
  // by. The search box is untouched above them: this is strictly additive.
  const towns = props.registry.options.airportPickups;

  if (p2p) {
    const ready = props.pickupLabel !== null && props.dropoffLabel !== null;
    return (
      <div class="screen-pane screen-pane--with-bar">
        <h1 class="screen-title" tabIndex={-1}>Where is the journey?</h1>
        <h2 class="field-heading">Pickup</h2>
        <LocationSearch
          placeholder="Pickup location"
          /* K5-6: the label names the ACTION, the placeholder names the VALUE.
             Distinct per end, because both searches share this one screen and
             two textboxes with the same accessible name is the defect. */
          label="Find the pickup"
          selectedLabel={props.pickupLabel}
          initialQuery={props.pickupText}
          routingKey={props.routingKey}
          onPick={props.onPickLocation}
          onClear={props.onClearLocation}
          onEscape={props.onEscapePickup}
          onMiss={props.onMiss}
          browse={{ towns, endLabel: 'Pickup' }}
        />
        <h2 class="field-heading">Drop-off</h2>
        <LocationSearch
          placeholder="Drop-off location"
          label="Find the drop-off"
          selectedLabel={props.dropoffLabel}
          initialQuery={props.dropoffText}
          onPick={props.onPickDropoff}
          onClear={props.onClearDropoff}
          onEscape={props.onEscapeDropoff}
          onMiss={props.onMiss}
          // ONE browse block at a time on this screen, and it follows the order
          // the journey is entered in: the drop-off chips appear once the pickup
          // is settled. Both blocks at once is 2x28 chips on a screen whose
          // stated failure mode is "too many buttons", for no gain — the
          // drop-off SEARCH is live from the first paint either way, so nothing
          // is withheld, only un-cluttered.
          browse={props.pickupLabel !== null ? { towns, endLabel: 'Drop-off' } : undefined}
        />
        <TotalBar
          label="Total"
          totalCents={null}
          primaryLabel="Continue"
          primaryDisabled={!ready}
          onPrimary={props.onContinue}
        />
      </div>
    );
  }

  // grid / airport
  const arrival = props.airportEnd?.direction === 'from-airport';
  const endWord = arrival ? 'Drop-off' : 'Pickup';
  return (
    <div class="screen-pane">
      <h1 class="screen-title" tabIndex={-1}>Where is the pickup?</h1>
      {/* T2090 - WHICH WAY, AND WHICH TERMINAL, before the town. Helen's Bray
          job of 2026-09-14 ("never thought of that") is the measured case: the
          airport tab asked only for the town, so every walk-in was recorded and
          alerted as a DEPARTURE, and a customer collected FROM the airport had
          the airport named as their destination. The rows live on this tab
          because the direction decides which end the town below IS: on an
          arrival the pickup is the terminal and the town is the drop-off, which
          is what the heading and the browse label say once a direction is
          pressed. The pressed state is `airportEndOf(job)`, read off the same
          `pickupTerminal` fact (T515) the record and the team alert key on, so
          the chips can never show one direction while the record persists the
          other. Chips are `Chip shape="filter"` in the shipped group idiom
          (BandControl), NOT a second `h2`: `location-browse.spec.tsx` pins one
          `h2.field-heading` on this tab (T1749 M8), so each row's name is its
          group's `aria-label`. A direction press passes `null`, which keeps the
          terminal the record already carries; a terminal press keeps the
          current direction. Nothing here is a priced input, so no re-price.
          ⚠️ The `h1` is net-locked and unchanged: on an arrival the pickup IS
          the airport, and the row above answers it (Q147 for Vicky's sitting). */}
      <div class="chip-row" role="group" aria-label="Direction">
        {(['to-airport', 'from-airport'] as const).map((direction) => (
          <Chip
            key={direction}
            shape="filter"
            pressed={props.airportEnd?.direction === direction}
            onClick={() => props.onSetAirportEnd?.(direction, null)}
          >
            {AIRPORT_DIRECTION_WORD[direction]}
          </Chip>
        ))}
      </div>
      <div class="chip-row" role="group" aria-label="Terminal">
        {AIRPORT_TERMINALS.map((token) => (
          <Chip
            key={token}
            shape="filter"
            pressed={props.airportEnd?.terminal === token}
            onClick={() => props.onSetAirportEnd?.(props.airportEnd?.direction ?? 'to-airport', token)}
          >
            {/* The airport's own signage for a terminal (the ONE designation
                map, service-schema.ts); 'not-sure' has none and says so. */}
            {terminalDesignation(token) ?? 'Not sure'}
          </Chip>
        ))}
      </div>
      {/* ⭐⭐ T1749 M8 (DS-23 a) — THE HEADING RHYTHM, EQUALISED. Every picker
          block on this screen now carries exactly ONE `h2.field-heading` naming
          what that block answers: point-to-point has `Pickup` and `Drop-off`
          (one per search, unchanged), golf's list and its `Other` panel each ask
          their end's question (`Where are we collecting?` / `Where are we going?`,
          unchanged), and this tab — the only block with NO heading at all — takes
          point-to-point's own word for the same field. Three tabs of one screen
          that structured their headings three different ways is the drift DS-23
          measures; the words are the existing copy voice, and nothing is renamed.
          ⚠️ NOT A RESTATEMENT OF THE `h1`: the h1 asks the screen's question and
          this names the field the answer goes in, which is exactly the split
          point-to-point has always drawn between `Where is the journey?` and its
          two headings.
          T2090: on an ARRIVAL the field is the drop-off (the pickup is the
          terminal pressed above), and the browse label below follows it. */}
      <h2 class="field-heading">{endWord}</h2>
      <LocationSearch
        placeholder={AIRPORT_SEARCH_PLACEHOLDER}
        /* K5-6: the placeholder is net-locked verbatim and says HOW to use the
           box; the visible label says what the box is for. */
        label="Find a place"
        selectedLabel={props.pickupLabel}
        initialQuery={props.pickupText}
        routingKey={props.routingKey}
        onPick={(id, label) => {
          props.onPickLocation(id, label);
          props.onContinue();
        }}
        onClear={props.onClearLocation}
        onEscape={(place) => {
          // Grid auto-advances on a pick; the hand-quote does the same so Helen
          // isn't left on a screen with nothing more to do.
          props.onEscapePickup(place);
          props.onContinue();
        }}
        onMiss={props.onMiss}
        browse={{ towns, endLabel: endWord }}
      />
    </div>
  );
}

/**
 * T660 M7's three-state route load (loading / error / ready) is GONE with the
 * fetch it guarded. S1 removed `/api/golf-routes` from this screen entirely —
 * the places arrive on the login-time registry, so there is no in-flight state
 * to misreport and no failed read to mistake for "this course does not exist".
 * The hazard that comment described is now absent by construction rather than
 * handled.
 */

/** The sentinel for "not one of our places" — never a real place name. */
const GOLF_OTHER = '__other__';

/**
 * T906 S1 — the golf journey, stated as a place at EACH END.
 *
 * Replaces a hub grid + a route grid, both derived from the `rates` table
 * itself, which meant a pair with no rate row had no tile, could not be
 * selected, and could not be quoted at all: the job could not be started, and
 * the empty state sent Helen to Price lists mid-call. Now every pair is
 * expressible; one that is priced prices from the grid exactly as before, and
 * one that is not lands on a hand price, which is the ruled behaviour.
 *
 * ⛔ ONE SHARED LIST, filling whichever end is active — not two lists. That is
 * the shape Alex approved (docs/prototypes/golf-pricing-concepts.html Concept D:
 * two collapsed rows, then a single list under "The one list. Adding a place
 * extends everything."), and it is also what keeps the e2e green lane honest:
 * `e2e/tests/green/quote-golf.spec.ts` clicks a hub by non-exact accessible
 * name as its FIRST action on this screen, so the same place rendered in two
 * live lists would be two matches and a strict-mode failure — before any
 * collapse could fire, and no name prefix would help (an `endLabel` prefix only
 * discriminates under `exact: true`).
 *
 * ⛔ AND NOT `LocationSearch`, despite the shape looking identical. Its search
 * box queries `/api/locations`, which is seeded only from the GAZETTEER and
 * holds no golf place — so typing "Druids" returns nothing and its fuzzy tier
 * offers a Wicklow TOWN. Its browse row also requires a `locationId` a golf
 * place deliberately does not have, and its escape wires to
 * `setPickupOffGazetteer`, which on golf destroys the price. Helen still gets
 * search; it just cannot be that component. The filter below is local, over the
 * ~10 places already in hand.
 */
type GolfEnd = 'pickup' | 'destination';

function GolfPlacePicker(props: {
  places: readonly GolfPlace[];
  heading: string;
  /** Seeds the field id, so two ends can never collide (see `fieldId`). */
  endLabel: string;
  exclude: string | null;
  onPick: (place: string) => void;
}) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const offered = props.places.filter((p) => p.name !== props.exclude);
  // The ≥2-char gate and the 6-hit cap are the shipped Rates-shelf idiom
  // (screens/admin/Shelf.tsx): the shelf's covers plus a search are the stated
  // Helen-test budget. (Board 938: that is Shelf.tsx's COMPONENT budget and it
  // is accurate; the shelf SCREEN is nine against DESIGN.md §1 rule 2's seven,
  // pinned in test/client/control-budget.spec.tsx. The idiom borrowed here is
  // the search behaviour, which is unaffected either way.)
  // The search tier REPLACES the browse tiles rather than
  // sitting beside them — rendered together, typing "Arklow" would show
  // Arklow Golf Club as a browse tile AND as a hit, which is the very
  // double-match one shared list exists to avoid.
  const searching = needle.length >= 2;
  // ⛔ THE TOTAL IS TAKEN BEFORE THE CAP - K5-6, T1723 M4d security review (LOW).
  // `.slice(0, 6)` cut the list and the live region then said "6 places
  // matching wick", so Helen was told there were six and the seventh course she
  // could hear the caller naming was not one of them. The cap stays (it is the
  // shipped Rates-shelf budget); what changes is that the region says it capped.
  // ⚠️ THE CAP IS THE SEARCH TIER'S ONLY. The browse tier renders every offered
  // place, so slicing unconditionally would silently hide places nobody filtered.
  const matches = searching ? offered.filter((p) => p.name.toLowerCase().includes(needle)) : offered;
  const shown = searching ? matches.slice(0, 6) : matches;
  /* K5-6 (ux-review WARN) - the fix M4a made on the airport filter, applied
     here: a REAL visible label, not placeholder-only labelling. `.field-label`
     is both the 20px/600 declaration and what style-contract.spec.ts's "no
     unclassed <label>" pin reads. The full reasoning is at
     components/LocationSearch.tsx:290-310; the short of it is that the
     `aria-label` IS GONE deliberately, because a `<label for>` IS the
     accessible name and keeping both would have the box say one thing on
     screen and another to a screen reader. The words are the airport filter's,
     so the two pickers read alike, and the END is already stated by the
     `.field-heading` directly above.
     ⚠️ Only one picker is mounted at a time (`active` is a single end), so the
     id could be a constant; it is end-derived anyway so that a future double
     mount cannot collide two ids and silently mislabel a box. */
  const fieldId = `golf-place-${props.endLabel.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`;
  return (
    <>
      <h2 class="field-heading">{props.heading}</h2>
      {/* ⭐⭐ T1749 M8 (DS-23 c) — THE FILTER BOX IS `FormField` NOW. The markup it
          replaces was this component's own hand-rolled pair: a `.field-label`
          with a `for`, and an `<input>` carrying `.loc-search.place-input`. The
          library composes exactly that pair — the same `.field-label`, the same
          `for`/`id` wiring, the same box (`.login-input` carries `.place-input`'s
          own declarations, token for token) — so the screen stops drawing a field
          and the hint/error slots come with it for free (§5.1's sourcing rule).
          ⚠️ `.place-input` SURVIVES AS A WRAPPER, and only as a width: the box is
          the library's and the width is this screen's placement, which is the one
          thing a surface sheet may still say.
          ⚠️ `kind=text` (the default), so the control is `type="text"` rather than
          the `type="search"` it was. That is the ruled adoption: `FormField`'s type
          union is the set the two tools actually use, and a `search` input's only
          real difference is a webkit clear glyph that duplicates the box Helen can
          already select and delete. The accessible name, the placeholder and the
          id are byte-identical, so every pin on this box still resolves. */}
      <div class="place-input">
        <FormField
          id={fieldId}
          label="Find a place"
          placeholder="Type to filter places..."
          value={q}
          onInput={setQ}
        />
      </div>
      {/* Filtering silently swapped 11 tiles for one sentence with nothing
          announced. Polite, so it never interrupts Helen mid-sentence.
          ⛔ The QUERY is in the message, not just the count, because a polite
          region only speaks when its text CHANGES: "wick" and "macr" both match
          exactly one place, so a bare count is byte-identical across two
          completely different result sets and the second one is never announced. */}
      <p class="visually-hidden" role="status" aria-live="polite">
        {searching
          ? matches.length > shown.length
            ? `${shown.length} of ${matches.length} places matching ${needle}`
            : `${shown.length} ${shown.length === 1 ? 'place' : 'places'} matching ${needle}`
          : ''}
      </p>
      {/* ⭐ K5-6 (T1723 M4i, ux-review WARN) - THE CAP IS VISIBLE TOO NOW.
          M4d made the polite region state it (`6 of 7 places matching wick`)
          and stopped there, so the SIGHTED operator still saw six tiles and
          nothing at all to say a seventh course existed - the same false
          picture the region was fixed to stop telling, on the other channel.
          ⛔ `aria-hidden`, because the region above already carries the count:
          two nodes with one sentence is that sentence read twice. This is the
          portal's split at RequestForm.tsx `#rf-resident-matches` (live region
          `.visually-hidden`, visible hint `aria-hidden`), reused rather than
          reinvented, and the region's text is deliberately untouched.
          ⚠️ `.helper-note` is REUSED rather than a hint class minted: it is this
          app's general quiet-message class (about sixty sites, loading lines
          included, not empty states only) and it is what the no-match line
          directly below uses in this same slot, so the two mutually exclusive
          messages read alike. No app.css rule was needed.
          ⚠️ No `searching &&` guard: off the search tier `shown` IS `matches`,
          so the condition is the whole of it. */}
      {matches.length > shown.length && (
        <p class="helper-note" aria-hidden="true">
          {`${shown.length} of ${matches.length} places shown. Keep typing to narrow it.`}
        </p>
      )}
      {/* ⭐ T1749 M8 (DS-23 c) — A LIST EMPTY IS `EmptyState` (§7.2). This one
          is the golf list's own: the filter matched nothing, so the list below is
          empty and the component whose whole job is to say that says it. The
          WORDS are unchanged. ⚠️ The two lines above it are NOT list empties — one
          is a cap notice over a list that has rows, the other a live region — so
          they stay `.helper-note` and keep the quiet-message look. */}
      {searching && shown.length === 0 && (
        <EmptyState
          /* ⭐ T1749 M8 round 4 (impeccable P2): NEUTRAL. The places exist — the
             FILTER is what hid them — so §7.2's good-news green would be an
             answer to a question nobody asked. Same reading as Bookings' search
             miss. ⛔ M9 rework 2: neutral is the DEFAULT now, so the prop that
             said it is gone rather than restating it. */
          headline="No place matches that."
          evidence="Use “Other” below to quote it by hand."
        />
      )}
      {/* ⭐⭐ T1749 M8 (DS-23 b) — ONE ROW SHAPE ACROSS THE THREE PICKER TABS.
          These were `.tile.tile--compact` in a `.tile-grid`: an 88px card grid
          for a list of place NAMES, beside a grid tab whose results are a row
          list (`ul.loc-results > li > button.loc-result`) and a point-to-point
          tab that uses the same one. Three tabs of one screen answering the same
          question in two shapes is the drift DS-23 measures — so the golf list
          takes the shape the other two already had: `--text-body` rows at
          `--target-preferred`, in the card the list draws once around all of them.
          ⛔ `Other / not listed` IS A ROW IN THE SAME LIST, unchanged in name and
          in behaviour: it is one of the answers to the question, not a control
          beside them, and six pins across the suite and the net name it.
          ⚠️ `.tile--compact` IS RETIRED WITH THIS LIST (it had no other wearer);
          `.tile` stays for Passengers and Home, which keep the card grid. */}
      <ul class="loc-results">
        {shown.map((p) => {
          // A CLOSED course stays on Helen's picker and says so, so she can tell
          // a caller "closed for a redesign, reopening 2027" rather than fail to
          // find it. This is why the place list reads the view RAW.
          const closed = p.kind === 'course' ? closureNoteFor(p.name) : null;
          return (
            <li key={p.name}>
              <button
                type="button"
                class="loc-result"
                onClick={() => props.onPick(p.name)}
              >
                {p.name}
                {closed !== null && <span class="closed-note">{closed}</span>}
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            class="loc-result"
            onClick={() => props.onPick(GOLF_OTHER)}
          >
            Other / not listed
          </button>
        </li>
      </ul>
    </>
  );
}

/**
 * The collapsed end row: which end this is, the chosen place, and a way back.
 *
 * ⛔ THE ROLE CAPTION IS NOT DECORATION. Without it the settled state is two
 * identical white rows and two identical "Change" buttons — and golf places are
 * symmetric, so either could plausibly be either end (Druids Glen Resort is both
 * a hub and a course). The `aria-label`s below already told a SCREEN-READER user
 * which was which while a sighted operator got nothing, which is the inversion
 * this fixes.
 */
function GolfEndRow(props: { role: string; label: string; value: string; onChange: () => void }) {
  return (
    <div class="loc-selected">
      <span class="loc-selected-body">
        <span class="loc-selected-role">{props.role}</span>
        <span class="loc-selected-label">{props.value}</span>
      </span>
      {/* Distinct accessible names: two bare "Change" buttons cannot be told
          apart by a contract-first spec, which may not use a test id. */}
      <button
        type="button"
        class="btn btn--quiet btn--inline loc-change"
        aria-label={`Change ${props.label}`}
        onClick={props.onChange}
      >
        Change
      </button>
    </div>
  );
}


function GolfVariant(props: LocationScreenProps) {
  const places = props.registry.options.golfPlaces ?? [];
  // Which end, if any, has been sent to free text. Derived state stays derived:
  // only this explicit operator choice is remembered, so a place arriving later
  // (or a Change) cannot leave a stale "Other" panel on screen.
  const [otherFor, setOtherFor] = useState<GolfEnd | null>(null);
  /**
   * The hand-typed text, remembered PER END rather than as one shared box.
   * Helen is on the phone: "Back to the list" and then Other again, or a Change
   * on an end she typed, both used to come back blank and cost her a retype of a
   * name the caller had already given her. Keyed by end so the two cannot
   * overwrite each other.
   */
  const [typedByEnd, setTypedByEnd] = useState<Record<GolfEnd, string>>({
    pickup: '',
    destination: '',
  });
  const setOtherText = (end: GolfEnd, v: string) =>
    setTypedByEnd((prev) => ({ ...prev, [end]: v }));

  const pickup = props.golfPickupPlace ?? null;
  const destination = props.golfDestinationPlace ?? null;

  const commit = (end: GolfEnd, place: string) => {
    props.onSetGolfEnd(end, place);
    setOtherFor(null);
    // The DESTINATION completes the journey, so it advances — matching the
    // airport variant, which auto-advances on its single pick, and keeping the
    // e2e green lane's hub -> course -> band sequence intact. The PICKUP must
    // not advance: the journey is only half stated.
    if (end === 'destination') props.onContinue();
  };

  const choose = (end: GolfEnd, place: string) => {
    if (place === GOLF_OTHER) {
      setOtherFor(end);
      // Seed from the end's current value the first time only; after that the
      // remembered text wins, so a Back-to-the-list round trip is lossless.
      const current = end === 'pickup' ? pickup : destination;
      if (typedByEnd[end] === '' && current !== null) setOtherText(end, current);
      return;
    }
    commit(end, place);
  };

  const clear = (end: GolfEnd) => {
    // Remember what was there, so a Change on a hand-typed end can offer it back
    // rather than making Helen retype it mid-call.
    const current = end === 'pickup' ? pickup : destination;
    if (current !== null) setOtherText(end, current);
    props.onSetGolfEnd(end, null);
    setOtherFor(null);
  };

  // Exactly one picker is live at a time, which is what keeps a place name to a
  // single node in the accessibility tree.
  const active: GolfEnd | null = pickup === null ? 'pickup' : destination === null ? 'destination' : null;

  /** Case- and space-insensitive, because this end is typed by hand. */
  const sameAsOtherEnd = (end: GolfEnd, typed: string): boolean => {
    const other = end === 'pickup' ? destination : pickup;
    return other !== null && typed.trim().toLowerCase() === other.trim().toLowerCase();
  };

  /* K5-6 (ux-review WARN), T1723 M4i - ONE SOURCE FOR THE `for`/`id` PAIR of
     the hand-typed box below, written as a function of the end for the reason
     M4h gave on the filter box: only one panel is mounted at a time (`otherFor`
     is a single end), so the id could be a constant, but deriving it from the
     end means a future double mount cannot collide two ids and silently
     mislabel a box. Same shape as `sameAsOtherEnd` above. */
  const otherFieldId = (end: GolfEnd) => `golf-other-${end}`;

  const otherPanel = (end: GolfEnd) => (
    <>
      <h2 class="field-heading">{end === 'pickup' ? 'Where are we collecting?' : 'Where are we going?'}</h2>
      <p class="helper-note">
        Not one of our places, so this journey is priced by hand.
      </p>
      {/* K5-6 (ux-review WARN), T1723 M4i - the SIBLING of the filter box M4h
          fixed at 5585ac6d, given the identical treatment: a REAL visible
          `<label for>` carrying `.field-label` (both the 20px/600 declaration
          and what style-contract.spec.ts's "no unclassed <label>" pin reads),
          the input carrying the id, and the `aria-label` GONE - a `<label for>`
          IS the accessible name, and keeping both would have the box say one
          thing on screen and another to a screen reader.
          ⭐ THE WORDS ARE THE OLD `aria-label`'S, TO THE BYTE. Deliberate: four
          `getByLabelText('Collection place')` / `('Destination place')` sites in
          golf-picker.spec.tsx name this box, and they keep matching THROUGH the
          label, so the accessible name does not move and only the sighted
          operator gains - she previously had a grey placeholder that vanishes
          the moment she types.
          ⚠️ Named by END, unlike the filter's "Find a place": both ends can be
          hand-typed, and the `.field-heading` above asks a question rather than
          naming the field, so a generic label would leave the end unstated. */}
      {/* T1749 M8 (DS-23 c): `FormField`, for the reason the filter box above
          states. The label's WORDS, the placeholder and the end-derived id are
          unchanged to the byte — four `getByLabelText('Collection place')` /
          `('Destination place')` sites name this box. */}
      <div class="place-input">
        <FormField
          id={otherFieldId(end)}
          label={end === 'pickup' ? 'Collection place' : 'Destination place'}
          placeholder="Name the place"
          value={typedByEnd[end]}
          onInput={(v) => setOtherText(end, v)}
        />
      </div>
      {sameAsOtherEnd(end, typedByEnd[end]) && (
        <p class="helper-note">
          That is already the other end of this journey. Name a different place.
        </p>
      )}
      <div class="btn-row">
        <button
          type="button"
          /* K5-1 (DESIGN.md §5): SECONDARY, not primary. This panel renders on the
             golf screen, which already carries the fixed bar's gold `Continue` --
             two golds at once was the one hard §5 breach in the whole flow. The
             name is pinned by golf-picker.spec.tsx; the class is not, so the
             demotion is a token and nothing else. */
          class="btn btn--secondary"
          // The tile list excludes the opposite end; without the same check here
          // the free-text path walks straight round it and books a journey that
          // does not move.
          disabled={typedByEnd[end].trim() === '' || sameAsOtherEnd(end, typedByEnd[end])}
          onClick={() => commit(end, typedByEnd[end].trim())}
        >
          Use this place
        </button>
        <button type="button" class="btn btn--quiet" onClick={() => setOtherFor(null)}>
          Back to the list
        </button>
      </div>
    </>
  );

  return (
    <div class="screen-pane screen-pane--with-bar">
      <h1 class="screen-title" tabIndex={-1}>Which golf run?</h1>
      {/* ALWAYS pickup above destination, never settled-above-active. Ordering
          them by which was chosen last put the DESTINATION row directly above
          the heading "Where are we collecting?", which reads as a label for it. */}
      {pickup !== null && (
        <GolfEndRow
          role="Collecting from" label="the collection place"
          value={pickup} onChange={() => clear('pickup')}
        />
      )}
      {destination !== null && (
        <GolfEndRow
          role="Going to" label="the destination"
          value={destination} onChange={() => clear('destination')}
        />
      )}
      {otherFor !== null && active !== null && otherPanel(otherFor)}
      {otherFor === null && active !== null && (
        <GolfPlacePicker
          places={places}
          heading={active === 'pickup' ? 'Where are we collecting?' : 'Where are we going?'}
          endLabel={active === 'pickup' ? 'the collection place' : 'the destination'}
          // The same place at both ends is a degenerate pair with no rate row,
          // so it could only ever produce a hand price for a journey that does
          // not move. Removed rather than explained — and from BOTH sides: after
          // "Change the collection place" the destination is already chosen, so
          // excluding only the pickup would let the operator re-pick it here.
          exclude={active === 'destination' ? pickup : destination}
          onPick={(place) => choose(active, place)}
        />
      )}
      <TotalBar
        label="Total"
        totalCents={null}
        primaryLabel="Continue"
        primaryDisabled={pickup === null || destination === null}
        onPrimary={props.onContinue}
      />
    </div>
  );
}
